import React, { useState, useContext, useEffect } from 'react';
import { withRouter } from 'react-router';
import moment from 'moment';
import { GlobalContext } from '../../global-context';

import {
  makeStyles,
  Container,
  Typography,
  Tooltip,
  Icon,
  Button,
  Tabs,
  Tab,
  Paper,
  Checkbox,
  Box,
  Chip,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import FilterListIcon from '@material-ui/icons/FilterList';
import GetAppIcon from '@material-ui/icons/GetApp';

import gql from 'graphql-tag';
import { Subscription } from 'react-apollo';

import Loading from '../utils/Loading';
import OrdersFilter from './orders/OrdersFilter';
import BoothIdLink from '../reusable/BoothIdLink';

import { AccordianTable, AccordianRow, TableSort } from '../reusable/AccordianTable';

import { ExportToCsv } from 'export-to-csv';

import fragments from '../utils/graphql/fragments';

const log = true;

// Helper component to manage the side effect of updating totalCount
const SubscriptionDataHandler = ({ subscriptionData, setTotalCount }) => {
  const count = subscriptionData?.orders_aggregate?.aggregate?.count;

  useEffect(() => {
    if (count !== undefined) {
      setTotalCount(prevTotalCount => {
        // Only update if the new count is different from the current state
        // to avoid potential unnecessary re-renders if count itself is stable but parent re-renders.
        return count === prevTotalCount ? prevTotalCount : count;
      });
    }
  }, [count, setTotalCount]); // Dependencies: count and setTotalCount

  return null; // This component does not render anything itself
};

const cap = str => {
  if (str) {
    if (!str.includes(` `)) return str.charAt(0).toUpperCase() + str.slice(1);
    else {
      let arr = str.split(` `);
      arr = arr.map(s => s.charAt(0).toUpperCase() + s.slice(1));
      return arr.join(` `);
    }
  }
};

const titleCase = str => {
  return str
    .toLowerCase()
    .split(' ')
    .map(function (word) {
      if (word.length > 0) {
        return word.replace(word[0], word[0].toUpperCase());
      } else {
        return '';
      }
    })
    .join(' ');
};

const getDefaultRange = () => {
  const localRange = localStorage.getItem(`orders-index-range`);
  if (localRange) return localRange;
  else return `year`;
};

const getDefaultStart = () => {
  const localRange = localStorage.getItem(`orders-index-range`);
  const localStart = localStorage.getItem(`orders-index-start`);
  if (localRange === 'any') {
    return moment.utc(moment('1900-01-01')).format();
  }
  if (localRange && localRange !== `custom`) return moment.utc(moment().startOf(`day`)).format();
  else if (localRange === `custom` && localStart) return localStart;
  else return moment.utc(moment().startOf(`day`).add(1, `week`)).format();
};

const getDefaultEnd = () => {
  const localRange = localStorage.getItem(`orders-index-range`);
  const localEnd = localStorage.getItem(`orders-index-end`);
  if (localRange === 'any') {
    return moment.utc(moment('2095-01-01')).format();
  }
  if (localRange && localRange !== `custom`) {
    const rangeValue = localRange === 'week' ? 7 : localRange === 'month' ? 30 : localRange === 'year' ? 365 : 1;
    return moment.utc(moment().startOf(`day`).add(rangeValue, 'days')).format();
  } else if (localRange === `custom` && localEnd) return localEnd;
  else return moment.utc(moment().startOf(`day`)).format();
};

const getDefaultNulls = () => {
  const localNulls = localStorage.getItem(`orders-null-event-dates`);
  return Boolean(localNulls || false);
};

const getDefaultDisable = () => {
  const localRange = localStorage.getItem(`orders-index-range`);
  if (localRange && localRange === `custom`) return false;
  else return true;
};

const defaultOrder = `desc`;
const defaultOrderBy = `createdat`;

// Format date like Shopify: "Today at 7:31 pm", "Yesterday at 6:29 pm", "Friday at 11:01 pm", or "May 4 at 1:02 pm"
const formatOrderDate = date => {
  if (!date) return '-';

  const orderDate = moment(date);
  const now = moment();

  // Today
  if (orderDate.isSame(now, 'day')) {
    return `Today at ${orderDate.format('h:mm A')}`;
  }

  // Yesterday
  if (orderDate.isSame(now.clone().subtract(1, 'day'), 'day')) {
    return `Yesterday at ${orderDate.format('h:mm A')}`;
  }

  // Within the last week, show day of week
  if (orderDate.isAfter(now.clone().subtract(7, 'days'))) {
    return `${orderDate.format('dddd')} at ${orderDate.format('h:mm A')}`;
  }

  // Older dates: show Month Day at time
  return `${orderDate.format('MMM D')} at ${orderDate.format('h:mm A')}`;
};

// Format other dates (event date, ship date) - uses MMM D, YYYY format for consistency
const formatDate = date => {
  if (!date) return '-';
  return moment(date).format('MMM D, YYYY');
};

function Orders(props) {
  const ctx = useContext(GlobalContext);
  const cls = useStyles();

  const [status, setStatus] = useState('Not Done');
  const [range, setRange] = useState(getDefaultRange());
  const [start, setStart] = useState(getDefaultStart());
  const [end, setEnd] = useState(getDefaultEnd());
  const [nulls, setNulls] = useState(getDefaultNulls());
  const [disablePickers, setDisablePickers] = useState(getDefaultDisable());
  const [tabValue, setTabValue] = useState(0);

  const [search, setSearch] = useState(``);
  const [order, setOrder] = useState(defaultOrder);
  const [orderBy, setOrderBy] = useState(defaultOrderBy);
  const [tablePage, setTablePage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20); // Match Shopify's default
  const [expandedRowId, setExpandedRowId] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [lastSeenOrderId, setLastSeenOrderId] = useState(0);
  const [isNewFilterActive, setIsNewFilterActive] = useState(false);

  // Parse URL query parameters and apply filters
  useEffect(() => {
    //console.log(`URL query parameters:`, props.location.search);

    const params = new URLSearchParams(props.location.search);

    //console.log(`Params:`, params.get('is_new'));

    // Reset pagination and search when URL changes
    setTablePage(0);
    setSearch('');
    setIsNewFilterActive(false); // Reset filter active state by default
    // lastSeenOrderId is reset by specific filters if needed, or remains 0

    // Default to general filters, specific params will override
    let ensureDefaultFilters = true;

    // Handle urgent filter first as it overrides date ranges
    if (params.get('urgent') === 'true') {
      setRange('any');
      setDisablePickers(true);
      setStart(moment.utc(moment('1900-01-01')).format());
      setEnd(moment.utc(moment('2095-01-01')).format());
      localStorage.setItem('orders-show-urgent', 'true');
      ensureDefaultFilters = false; // Urgent sets its own dates
    } else {
      localStorage.removeItem('orders-show-urgent');
    }

    // Handle new orders filter
    if (params.get('is_new') === 'true') {
      //setStatus('Pending'); // Status will be set by tab 1 logic
      console.log(`Setting last seen order ID:`, localStorage.getItem('highestOrderIdSeen'));
      setLastSeenOrderId(parseInt(localStorage.getItem('highestOrderIdSeen') || '', 10));
      setIsNewFilterActive(true); // Set filter active state
      setTabValue(1); // Set to Pending tab
      setStatus('Pending'); // Explicitly set status
      // Apply standard filters for 'Pending' tab (tab 1)
      const pendingRange = getDefaultRange();
      setRange(pendingRange);
      if (pendingRange === 'custom') {
        setStart(getDefaultStart());
        setEnd(getDefaultEnd());
        setDisablePickers(false);
      } else {
        if (pendingRange === 'any') {
          setStart(moment.utc(moment('1900-01-01')).format());
          setEnd(moment.utc(moment('2095-01-01')).format());
        } else {
          setStart(moment.utc(moment().startOf('day')).format());
          const rangeMap = { week: 7, month: 30, year: 365 };
          const rangeValue =
            rangeMap[pendingRange] ||
            (pendingRange === 'year' ? 365 : pendingRange === 'month' ? 30 : pendingRange === 'week' ? 7 : 1);
          setEnd(moment.utc(moment().startOf('day').add(rangeValue, 'days')).format());
        }
        setDisablePickers(true);
      }
      setNulls(getDefaultNulls());
      ensureDefaultFilters = false;
    } else if (params.get('recent') === 'true') {
      setStatus('Done');
      setRange('custom');
      setStart(moment().subtract(2, 'weeks').startOf('day').format());
      setEnd(moment().endOf('day').format());
      setDisablePickers(true);
      setNulls(false);
      setTabValue(6); // New "Recent" tab index
      ensureDefaultFilters = false;
    } else {
      // Handle status filter from URL if no other specific filter like 'is_new' or 'recent' is active
      const statusParam = params.get('status');
      if (statusParam) {
        setStatus(statusParam);
        // Set corresponding tab
        if (statusParam === 'All' || statusParam === 'Not Done') setTabValue(0);
        else if (statusParam === 'Pending' || ['New', 'Ready'].includes(statusParam)) setTabValue(1);
        else if (statusParam === 'Shipping' || ['Preparing', 'Outbound'].includes(statusParam)) setTabValue(2);
        else if (statusParam === 'WithCustomer' || ['Delivered', 'Packing'].includes(statusParam)) setTabValue(3);
        else if (statusParam === 'Inbound' || ['Inbound', 'Returned', 'Checkin'].includes(statusParam)) setTabValue(4);
        else if (statusParam === 'Done' || statusParam === 'Canceled') setTabValue(5);
        ensureDefaultFilters = false; // Status from URL implies specific tab, whose defaults will apply via tabValue change or initial load
      }
      // Additional dashboard links might use these parameters
      // These also set status and tabValue, and should prevent default filter application
      else if (params.get('pending') === 'true') {
        setStatus('Pending');
        setTabValue(1);
        ensureDefaultFilters = false;
      } else if (params.get('shipping') === 'true') {
        setStatus('Shipping');
        setTabValue(2);
        ensureDefaultFilters = false;
      } else if (params.get('with_customer') === 'true') {
        setStatus('WithCustomer');
        setTabValue(3);
        ensureDefaultFilters = false;
      } else if (params.get('inbound') === 'true') {
        setStatus('Inbound');
        setTabValue(4);
        ensureDefaultFilters = false;
      } else if (params.get('done') === 'true') {
        setStatus('Done');
        setTabValue(5);
        ensureDefaultFilters = false;
      }
    }

    // If no specific parameters set the filters, apply defaults for the current tabValue
    // This primarily ensures that on initial load without specific params, tab 0 (All/Not Done) gets its filters.
    // Or if a URL sets only tabValue (e.g. via statusParam) but not full date filters.
    if (ensureDefaultFilters) {
      // Apply standard date/filter settings based on the current tabValue
      // This logic is now part of handleTabChange, so we need to ensure it's effectively called or replicated.
      // For initial load, useState initial values should align with tab 0.
      // If tabValue was changed by a simple param (e.g. status=Done which sets tabValue=5),
      // this block ensures the date filters for tab 5 are applied.
      // Let's simulate a call to the core logic of setStandardDateFilters for the current tabValue:
      const currentRange = getDefaultRange(); // Use general default range
      // If tabValue is for a tab that uses standard filters:
      if (tabValue !== 6) {
        // Assuming tab 6 is "Recent" and has special handling
        setStatus(TABS_CONFIG[tabValue]?.status || 'Not Done'); // Get status for current tab
        setRange(currentRange);
        if (currentRange === 'custom') {
          setStart(getDefaultStart());
          setEnd(getDefaultEnd());
          setDisablePickers(false);
        } else {
          if (currentRange === 'any') {
            setStart(moment.utc(moment('1900-01-01')).format());
            setEnd(moment.utc(moment('2095-01-01')).format());
          } else {
            setStart(moment.utc(moment().startOf('day')).format());
            const rangeMap = { week: 7, month: 30, year: 365 };
            const rangeValue = rangeMap[currentRange] || rangeMap.day;
            setEnd(moment.utc(moment().startOf('day').add(rangeValue, 'days')).format());
          }
          setDisablePickers(true);
        }
        setNulls(getDefaultNulls());
      }
    }
  }, [props.location.search]); // Removed tabValue and handleTabChange from deps to simplify and avoid loops.

  // Define TABS_CONFIG for easier status mapping, used in useEffect
  const TABS_CONFIG = {
    0: { status: 'Not Done' },
    1: { status: 'Pending' },
    2: { status: 'Shipping' },
    3: { status: 'WithCustomer' },
    4: { status: 'Inbound' },
    5: { status: 'Done' }, // Archived/Done
    6: { status: 'Done' }, // Recently Completed
  };

  // Control range picker
  const handleRangeChange = value => {
    log && console.log(`Range Change:`, value);
    localStorage.setItem(`orders-index-range`, value);
    if (value !== `custom`) {
      setDisablePickers(true);
      setRange(value);
      if (value === 'any') {
        setStart(moment.utc(moment('1900-01-01')).format());
        setEnd(moment.utc(moment('2095-01-01')).format());
      } else {
        setStart(moment.utc(moment().startOf(`day`)).format());
        const rangeValue = value === 'week' ? 7 : value === 'month' ? 30 : value === 'year' ? 365 : 1;
        setEnd(moment.utc(moment().startOf(`day`).add(rangeValue, 'days')).format());
      }
    } else {
      setDisablePickers(false);
      setRange(value);
      setStart(getDefaultStart());
      setEnd(getDefaultEnd());
    }
    // Reset pagination when changing filters
    setTablePage(0);
  };

  //Handle the setting of allow nulls for event date filtering
  const handleNullsChange = value => {
    log && console.log(`Allow Nulls Change:`, value);
    localStorage.setItem(`orders-null-event-dates`, value);
    setNulls(value);
    // Reset pagination when changing filters
    setTablePage(0);
  };

  // Control date pickers
  const handleDateChange = (value, name) => {
    log && console.log(`Date Change:`, { value, name });
    if (name === `start`) {
      const newDate = moment.utc(moment(value).startOf(`day`)).format();
      localStorage.setItem(`orders-index-start`, newDate);
      setStart(newDate);
    } else {
      const newDate = moment.utc(moment(value).startOf(`day`)).format();
      localStorage.setItem(`orders-index-end`, newDate);
      setEnd(newDate);
    }
    // Reset pagination when changing filters
    setTablePage(0);
  };

  // Handle status changes
  const handleStatusChange = newStatus => {
    setStatus(newStatus);
    // Reset pagination when changing filters
    setTablePage(0);
  };

  // Handle search input changes
  const handleSearchChange = value => {
    setSearch(value);
    // Reset pagination when changing filters
    setTablePage(0);
  };

  // Handle page change - will trigger new data fetch
  const handlePageChange = newPage => {
    setTablePage(newPage);
  };

  // Handle rows per page change
  const handleRowsPerPageChange = newRowsPerPage => {
    setRowsPerPage(newRowsPerPage);
    setTablePage(0); // Reset to first page when changing rows per page
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setTablePage(0); // Reset pagination on any tab change

    // Helper to set standard date/filter configurations
    const setStandardDateFilters = () => {
      const currentRange = getDefaultRange();
      setRange(currentRange);

      if (currentRange === 'custom') {
        setStart(getDefaultStart());
        setEnd(getDefaultEnd());
        setDisablePickers(false);
      } else {
        setDisablePickers(true);
        if (currentRange === 'any') {
          setStart(moment.utc(moment('1900-01-01')).format());
          setEnd(moment.utc(moment('2095-01-01')).format());
        } else {
          // week, month, year, day (default to day if not matched)
          setStart(moment.utc(moment().startOf('day')).format());
          const rangeMap = { week: 7, month: 30, year: 365, day: 1 };
          const rangeValue = rangeMap[currentRange] || rangeMap.day;
          setEnd(moment.utc(moment().startOf('day').add(rangeValue, 'days')).format());
        }
      }
      setNulls(getDefaultNulls()); // Reset to default nulls handling for standard tabs
    };

    switch (newValue) {
      case 0: // All
        setStatus('Not Done');
        setStandardDateFilters();
        break;
      case 1: // Pending (null, New, Ready)
        setStatus('Pending');
        setStandardDateFilters();
        break;
      case 2: // Shipping (Preparing, Outbound)
        setStatus('Shipping');
        setStandardDateFilters();
        break;
      case 3: // With Customer (Delivered, Packing)
        setStatus('WithCustomer');
        setStandardDateFilters();
        break;
      case 4: // Inbound (Inbound, Returned, Checkin)
        setStatus('Inbound');
        setStandardDateFilters();
        break;
      case 5: // Archived/Done
        setStatus('Done');
        setStandardDateFilters();
        break;
      case 6: // Recently Completed (NEW TAB)
        setStatus('Done'); // Status is 'Done'
        setRange('custom'); // Technically custom, but fixed
        setStart(moment().subtract(2, 'weeks').startOf('day').format());
        setEnd(moment().endOf('day').format());
        setDisablePickers(true); // Pickers are disabled for this fixed range
        setNulls(false); // We need event_date for "recently completed"
        break;
      default:
        setStatus('All'); // Fallback, should ideally not be reached
        setStandardDateFilters();
    }
  };

  const handleSortOrderChange = (newOrderBy, newOrder) => {
    setOrder(newOrder);
    setOrderBy(newOrderBy);
    setTablePage(0);
  };

  const handleSelectAll = (event, ordersList) => {
    if (event.target.checked && ordersList && ordersList.length > 0) {
      // Select all orders on current page
      const newSelected = ordersList.map(order => order.id);
      setSelectedOrders(newSelected);
    } else {
      setSelectedOrders([]);
    }
  };

  const handleSelectOrder = id => {
    const selectedIndex = selectedOrders.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = [...selectedOrders, id];
    } else {
      newSelected = selectedOrders.filter(orderId => orderId !== id);
    }

    setSelectedOrders(newSelected);
  };

  const isSelected = id => selectedOrders.indexOf(id) !== -1;

  const applyFilters = data => {
    //console.log(`Applying filters to data:`, data);
    // First filter by search term if present
    let filteredData = data;
    if (search && search.length >= 1) {
      filteredData = data.filter(o => {
        if (
          (o.event_name && o.event_name.toLocaleLowerCase().includes(search)) ||
          (o.first_name && o.first_name.toLocaleLowerCase().includes(search)) ||
          (o.last_name && o.last_name.toLocaleLowerCase().includes(search)) ||
          (o.id && (o.id + ``).toLocaleLowerCase().includes(search)) ||
          (o.shipping_address1 && (o.shipping_address1 + ``).toLocaleLowerCase().includes(search)) ||
          (o.shipping_address2 && (o.shipping_address2 + ``).toLocaleLowerCase().includes(search)) ||
          (o.shipping_city && (o.shipping_city + ``).toLocaleLowerCase().includes(search)) ||
          (o.shipping_state && (o.shipping_state + ``).toLocaleLowerCase().includes(search)) ||
          (o.shipping_zip && (o.shipping_zip + ``).toLocaleLowerCase().includes(search)) ||
          (o.outbound_tracking && (o.outbound_tracking + ``).toLocaleLowerCase().includes(search)) ||
          (o.inbound_tracking && (o.inbound_tracking + ``).toLocaleLowerCase().includes(search)) ||
          (o.email && (o.email + ``).toLocaleLowerCase().includes(search)) ||
          (o.first_name && (o.first_name + ``).toLocaleLowerCase().includes(search)) ||
          (o.last_name && (o.last_name + ``).toLocaleLowerCase().includes(search)) ||
          (o.extra_tracking && (o.extra_tracking + ``).toLocaleLowerCase().includes(search)) ||
          (o.booth_id && (o.booth_id + ``).toLocaleLowerCase().includes(search))
        ) {
          return true;
        } else return false;
      });
    }

    // Then filter for urgent orders if that filter is active
    const showUrgent = localStorage.getItem('orders-show-urgent') === 'true';
    if (showUrgent) {
      // Apply the same urgent criteria as in the Dashboard component
      filteredData = filteredData.filter(order => {
        const today = moment().startOf('day');

        // Shipping today or past ship date
        if (
          (['New', 'Ready'].includes(order.status) ||
            (order.status === 'Outbound' && !order.outbound_shipment_activity?.shipped)) &&
          order.ship_date
        ) {
          const shipDate = moment(order.ship_date);
          if (shipDate.isSame(today, 'day') || shipDate.isBefore(today, 'day')) {
            return true;
          }
        }

        // Events starting today
        if (
          (['New', 'Ready'].includes(order.status) ||
            (order.status === 'Outbound' && !order.outbound_shipment_activity?.shipped)) &&
          order.event_date
        ) {
          const eventDate = moment(order.event_date);
          if (eventDate.isSame(today, 'day')) {
            return true;
          }
        }

        // Late returns
        if (['Delivered', 'Packing'].includes(order.status) && order.return_date) {
          const returnDate = moment(order.return_date);
          if (returnDate.isBefore(today, 'day')) {
            return true;
          }
        }

        // Need check-in
        if (order.status === 'Returned') {
          return true;
        }

        return false;
      });
    }

    if (lastSeenOrderId > 0) {
      console.log(`Last seen order ID:`, lastSeenOrderId);
      filteredData = filteredData.filter(order => order.id > lastSeenOrderId);
    }

    return filteredData;
  };

  const generateCSV = orders => {
    const createCsvRow = order => {
      return {
        ID: order.id,
        NAME: `${order.first_name || ''} ${order.last_name || ''}`.trim() || '-',
        DATE_PLACED: order.createdat ? moment.utc(moment(order.createdat)).format(`YYYY-MM-DD`) : `-`,
        EVENT_DATE: order.event_date ? moment.utc(moment(order.event_date)).format(`YYYY-MM-DD`) : `-`,
        SHIP_DATE: order.ship_date ? moment.utc(moment(order.ship_date)).format(`YYYY-MM-DD`) : `-`,
        STATUS: order.status,
        TRANSIT_DAYS: order.transit_days || '-',
        CUSTOMER: `${order.first_name || ''} ${order.last_name || ''}`.trim() || '-',
        LOCATION: `${titleCase(order.shipping_city || '')}, ${order.shipping_state || ''}`,
      };
    };
    const csvRows = orders.map(order => createCsvRow(order));
    const csvOptions = {
      filename: `BBM_orders_export_${moment().format('YYYY-MM-DD')}`,
      showTitle: true,
      title: `Boothby Mail orders export`,
      useKeysAsHeaders: true,
    };

    // Create and generate the CSV
    const csvExporter = new ExportToCsv(csvOptions);
    csvExporter.generateCsv(csvRows);
  };

  const goToOrder = orderId => {
    props.history.push(`/orders/${orderId}`);
  };

  const getTableActions = orders => {
    return [{ label: `Export CSV`, handler: () => generateCSV(orders) }];
  };

  const renderTable = filteredOrders => {
    const rows = filteredOrders.map(order => {
      return {
        id: order.id,
        createdat: formatOrderDate(order.createdat),
        EVENT_DATE: formatDate(order.event_date),
        ship_date: formatDate(order.ship_date),
        NAME: `${order.first_name || ''} ${order.last_name || ''}`.trim() || '-',
        TRANSIT_DAYS: order.transit_days || '-',
        STATUS: order.status,
        BOOTH_ID: order.booth_id,
        order: order,
      };
    });

    // Function to handle column sorting
    const handleSort = columnId => {
      const isAsc = orderBy === columnId && order === 'asc';
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderBy(columnId);
      setTablePage(0); // Reset to first page when changing sort
    };

    // Render sort indicator
    const renderSortIndicator = columnId => {
      const isSorted = orderBy === columnId;
      return (
        <span className={cls.sortIndicator}>
          {isSorted ? (
            order === 'asc' ? (
              <span className={cls.sortIconAsc}>▲</span>
            ) : (
              <span className={cls.sortIconDesc}>▼</span>
            )
          ) : null}
        </span>
      );
    };

    return (
      <div className={cls.tableWrapper}>
        <table className={cls.table}>
          <thead>
            <tr>
              <th className={cls.checkboxCell}>
                <Checkbox
                  indeterminate={selectedOrders.length > 0 && selectedOrders.length < filteredOrders.length}
                  checked={filteredOrders.length > 0 && selectedOrders.length === filteredOrders.length}
                  onChange={e => handleSelectAll(e, filteredOrders)}
                  inputProps={{ 'aria-label': 'select all orders' }}
                  className={cls.smallCheckbox}
                />
              </th>
              <th className={`${cls.orderNumberCell} ${cls.sortableHeader}`} onClick={() => handleSort('id')}>
                Order {renderSortIndicator('id')}
              </th>
              <th className={`${cls.dateCell} ${cls.sortableHeader}`} onClick={() => handleSort('createdat')}>
                Date {renderSortIndicator('createdat')}
              </th>
              <th className={`${cls.eventNameCell} ${cls.sortableHeader}`} onClick={() => handleSort('name')}>
                Name {renderSortIndicator('name')}
              </th>
              <th className={`${cls.eventDateCell} ${cls.sortableHeader}`} onClick={() => handleSort('event_date')}>
                Event Date {renderSortIndicator('event_date')}
              </th>
              <th className={`${cls.shipDateCell} ${cls.sortableHeader}`} onClick={() => handleSort('ship_date')}>
                Ship Date {renderSortIndicator('ship_date')}
              </th>
              <th className={`${cls.transitTimeCell} ${cls.sortableHeader}`} onClick={() => handleSort('transit_days')}>
                Transit Time {renderSortIndicator('transit_days')}
              </th>
              <th className={`${cls.boothIdCell} ${cls.sortableHeader}`} onClick={() => handleSort('booth_id')}>
                Booth ID {renderSortIndicator('booth_id')}
              </th>
              <th className={`${cls.statusCell} ${cls.sortableHeader}`} onClick={() => handleSort('status')}>
                Status {renderSortIndicator('status')}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              const isItemSelected = isSelected(row.order.id);

              return (
                <tr
                  key={`order-${row.order.id}`}
                  onClick={() => goToOrder(row.order.id)}
                  className={`${cls.tableRow} ${index % 2 === 1 ? cls.alternateRow : ''}`}
                >
                  <td className={cls.checkboxCell}>
                    <Checkbox
                      checked={isItemSelected}
                      onClick={e => {
                        e.stopPropagation();
                        handleSelectOrder(row.order.id);
                      }}
                      inputProps={{ 'aria-labelledby': `order-${row.order.id}` }}
                      className={cls.smallCheckbox}
                      size='small'
                    />
                  </td>
                  <td className={cls.orderNumberCell}>
                    <span className={cls.orderNumber}>#{row.order.id}</span>
                  </td>
                  <td className={cls.dateCell}>{row.createdat}</td>
                  <td className={cls.eventNameCell}>{row.NAME}</td>
                  <td className={cls.eventDateCell}>{row.EVENT_DATE}</td>
                  <td className={cls.shipDateCell}>{row.ship_date}</td>
                  <td className={cls.transitTimeCell}>{row.TRANSIT_DAYS} days</td>
                  <td className={cls.boothIdCell}>
                    {row.BOOTH_ID ? <BoothIdLink boothId={row.BOOTH_ID} size='small' /> : '-'}
                  </td>
                  <td className={cls.statusCell}>
                    <Chip
                      label={row.STATUS}
                      className={`${cls.statusChip} ${cls[`status${row.STATUS.replace(/\s+/g, '')}`]}`}
                      size='small'
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div className={cls.pagination}>
          <span>{`${tablePage * rowsPerPage + 1}-${Math.min(
            (tablePage + 1) * rowsPerPage,
            totalCount
          )} of ${totalCount}`}</span>
          <div className={cls.paginationControls}>
            <Button
              disabled={tablePage === 0}
              onClick={() => handlePageChange(tablePage - 1)}
              className={cls.paginationButton}
            >
              Previous
            </Button>
            <Button
              disabled={(tablePage + 1) * rowsPerPage >= totalCount}
              onClick={() => handlePageChange(tablePage + 1)}
              className={cls.paginationButton}
            >
              Next
            </Button>
          </div>
        </div>
      </div>
    );
  };

  // Add a logging effect to help debug pagination issues
  useEffect(() => {
    console.log(`Current page: ${tablePage}, Rows per page: ${rowsPerPage}, Status: ${status}`);
  }, [tablePage, rowsPerPage, status]);

  // Ensure pagination resets when changing status tab
  useEffect(() => {
    setTablePage(0);
  }, [status, lastSeenOrderId]);

  const handleRemoveNewFilter = () => {
    const params = new URLSearchParams(props.location.search);
    params.delete('is_new');
    props.history.push({ search: params.toString() });
    setIsNewFilterActive(false);
    setLastSeenOrderId(0); // Reset lastSeenOrderId to fetch all relevant orders for the tab
    // The useEffect watching props.location.search will handle resetting tablePage and search
  };

  return (
    <div className={cls.root}>
      <Container maxWidth='lg'>
        <Box className={cls.headerSection}>
          <Typography variant='h5' component='h1' className={cls.title}>
            Orders
          </Typography>
          <Box className={cls.headerActions}>
            <Button
              variant='outlined'
              color='primary'
              size='small'
              onClick={isNewFilterActive ? handleRemoveNewFilter : undefined}
              disabled={!isNewFilterActive}
              className={cls.removeFilterButton}
              startIcon={<FilterListIcon />}
            >
              {isNewFilterActive ? 'Remove Filter' : 'No Filter'}
            </Button>
            <Button
              variant='outlined'
              color='default'
              className={cls.exportButton}
              startIcon={<GetAppIcon />}
              onClick={() => generateCSV([])}
              disabled={true}
            >
              Export
            </Button>
            <Button variant='contained' color='primary' className={cls.createButton} disabled={true}>
              Create order
            </Button>
          </Box>
        </Box>

        <Paper className={cls.tableContainer}>
          <div className={cls.tabsAndSearchContainer}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              indicatorColor='primary'
              textColor='primary'
              className={cls.tabs}
              variant='scrollable'
              scrollButtons='auto'
            >
              <Tab label='All' className={cls.tab} />
              <Tab label='Pending' className={cls.tab} />
              <Tab label='Shipping' className={cls.tab} />
              <Tab label='With Customer' className={cls.tab} />
              <Tab label='Inbound' className={cls.tab} />
              <Tab label='Archived' className={cls.tab} />
              <Tab label='Recent' className={cls.tab} />
            </Tabs>

            <div className={cls.searchContainer}>
              <div className={cls.searchBox}>
                <SearchIcon className={cls.searchIcon} />
                <input
                  type='text'
                  placeholder='Search orders...'
                  value={search}
                  onChange={e => handleSearchChange(e.target.value)}
                  className={cls.searchInput}
                />
                {search && (
                  <Button size='small' className={cls.clearButton} onClick={() => handleSearchChange('')}>
                    Clear
                  </Button>
                )}
              </div>
            </div>
          </div>

          <Subscription
            subscription={GET_ORDERS(status, nulls, lastSeenOrderId, orderBy, order)}
            variables={{
              startDate: start,
              endDate: end,
              limit: rowsPerPage,
              offset: tablePage * rowsPerPage,
              ...(status !== 'All' &&
              status !== 'Not Done' &&
              status !== 'Pending' &&
              status !== 'Shipping' &&
              status !== 'WithCustomer' &&
              status !== 'Inbound'
                ? { status: status }
                : {}),
            }}
          >
            {result => {
              const { loading, error, data } = result;
              if (loading) return <Loading fixed />;
              if (error) {
                console.log(`Failed to retrieve orders:`, error);
                ctx.handleNotifications(true, `error`, `Failed to retrieve orders: ${error.toString()}`);
                return (
                  <div className={cls.notFound}>
                    <Typography className={cls.notFoundTxt}>ERROR FINDING ORDERS</Typography>
                  </div>
                );
              }

              if (data && data.orders && data.orders.length > 0) {
                //log && console.log(`Orders for orders index:`, data.orders);

                // Update total count from aggregate query if available - MOVED to SubscriptionDataHandler
                // if (data.orders_aggregate && data.orders_aggregate.aggregate) {
                //   setTotalCount(data.orders_aggregate.aggregate.count);
                // }

                const filteredOrders = applyFilters(data.orders);

                return (
                  <>
                    <SubscriptionDataHandler subscriptionData={data} setTotalCount={setTotalCount} />
                    {renderTable(filteredOrders)}
                  </>
                );
              } else
                return (
                  <>
                    <SubscriptionDataHandler subscriptionData={data} setTotalCount={setTotalCount} />
                    <div className={cls.notFound}>
                      <Typography className={cls.notFoundTxt}>NO ORDERS FOUND</Typography>
                    </div>
                  </>
                );
            }}
          </Subscription>
        </Paper>
      </Container>
    </div>
  );
}

const useStyles = makeStyles(theme => ({
  root: {
    display: 'block',
    position: 'relative',
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
    background: '#f6f6f7',
    minHeight: '100vh',
    [theme.breakpoints.down('sm')]: {
      paddingTop: theme.spacing(3),
      paddingBottom: theme.spacing(3),
    },
    [theme.breakpoints.down('xs')]: {
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(2),
    },
  },
  headerSection: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
  },
  title: {
    fontWeight: 600,
    fontSize: '1.25rem',
  },
  headerActions: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  exportButton: {
    textTransform: 'none',
  },
  createButton: {
    textTransform: 'none',
    backgroundColor: '#008060',
    '&:hover': {
      backgroundColor: '#006e52',
    },
  },
  tableContainer: {
    borderRadius: 4,
    overflow: 'hidden',
  },
  tabsAndSearchContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: '1px solid #e5e5e5',
  },
  tabs: {
    minHeight: 48,
    flex: 1,
  },
  tab: {
    textTransform: 'none',
    minWidth: 100,
    minHeight: 48,
    fontSize: '0.875rem',
  },
  tableWrapper: {
    width: '100%',
    overflow: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    tableLayout: 'fixed',
    '& th': {
      padding: theme.spacing(0.5, 0.75),
      textAlign: 'left',
      fontSize: '0.75rem',
      fontWeight: 500,
      color: '#6d7175',
      borderBottom: '1px solid #e5e5e5',
      whiteSpace: 'nowrap',
      height: '32px',
    },
    '& td': {
      padding: theme.spacing(0.5, 0.75),
      fontSize: '0.813rem',
      borderBottom: '1px solid #e5e5e5',
      color: '#202223',
      height: '34px',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  },
  tableRow: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
  },
  alternateRow: {
    backgroundColor: '#f9fafb',
  },
  checkboxCell: {
    width: 28,
    textAlign: 'center',
    padding: '0',
  },
  smallCheckbox: {
    padding: 0,
  },
  orderNumberCell: {
    width: '60px',
  },
  dateCell: {
    width: '140px',
  },
  eventNameCell: {
    width: '20%',
  },
  eventDateCell: {
    width: '100px',
  },
  shipDateCell: {
    width: '100px',
  },
  transitTimeCell: {
    width: '80px',
    textAlign: 'center',
  },
  boothIdCell: {
    width: '110px',
  },
  statusCell: {
    width: '90px',
    textAlign: 'center',
  },
  orderNumber: {
    color: '#2c6ecb',
    fontWeight: 500,
  },
  statusChip: {
    fontSize: '0.7rem',
    height: 18,
    fontWeight: 500,
    borderRadius: 4,
    padding: '0 6px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
  },
  statusNew: {
    backgroundColor: 'rgba(255, 197, 0, 0.12)',
    color: '#996500',
  },
  statusReady: {
    backgroundColor: 'rgba(0, 128, 96, 0.12)',
    color: '#008060',
  },
  statusOutbound: {
    backgroundColor: 'rgba(0, 164, 255, 0.12)',
    color: '#0072b1',
  },
  statusDelivered: {
    backgroundColor: 'rgba(145, 88, 255, 0.12)',
    color: '#5c00ce',
  },
  statusReturned: {
    backgroundColor: 'rgba(66, 63, 62, 0.12)',
    color: '#403e3d',
  },
  statusDone: {
    backgroundColor: 'rgba(0, 128, 96, 0.12)',
    color: '#008060',
  },
  statusCanceled: {
    backgroundColor: 'rgba(220, 38, 38, 0.12)',
    color: '#d72c0d',
  },
  pagination: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1),
    borderTop: '1px solid #e5e5e5',
    fontSize: '0.875rem',
    color: '#6d7175',
  },
  paginationControls: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  paginationButton: {
    textTransform: 'none',
    fontSize: '0.875rem',
    minWidth: 'auto',
  },
  notFound: {
    padding: theme.spacing(4),
    textAlign: 'center',
  },
  notFoundTxt: {
    color: theme.palette.text.secondary,
    fontSize: '1rem',
  },
  sortableHeader: {
    cursor: 'pointer',
    userSelect: 'none',
    position: 'relative',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
  },
  sortIndicator: {
    display: 'inline-block',
    marginLeft: '4px',
    fontSize: '10px',
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
  },
  sortIconAsc: {
    color: '#008060',
  },
  sortIconDesc: {
    color: '#008060',
  },
  searchContainer: {
    padding: theme.spacing(1),
    display: 'flex',
    justifyContent: 'flex-end',
  },
  searchBox: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    width: '300px',
    backgroundColor: '#f5f5f5',
    borderRadius: '4px',
    border: '1px solid #e0e0e0',
  },
  searchIcon: {
    position: 'absolute',
    left: theme.spacing(1),
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#6d7175',
    fontSize: '1.2rem',
  },
  searchInput: {
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(10),
    height: '36px',
    width: '100%',
    border: 'none',
    backgroundColor: 'transparent',
    fontSize: '0.875rem',
    '&:focus': {
      outline: 'none',
    },
  },
  clearButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: '50%',
    transform: 'translateY(-50%)',
    minWidth: 'auto',
    textTransform: 'none',
    padding: theme.spacing(0.5, 1),
    fontSize: '0.75rem',
  },
  removeFilterButton: {
    textTransform: 'none',
  },
}));

const GET_ORDERS = (status, nulls, lastSeenOrderId, orderBy = 'createdat', order = 'desc') => {
  // console.log(`GET_ORDERS(${status}, ${nulls}, ${lastSeenOrderId}, ${orderBy}, ${order})`, {
  //   status,
  //   nulls,
  //   lastSeenOrderId,
  //   orderBy,
  //   order,
  // });

  // Map UI column names to database column names if needed
  const columnMapping = {
    id: 'id',
    createdat: 'createdat',
    name: 'event_name',
    event_date: 'event_date',
    ship_date: 'ship_date',
    transit_days: 'transit_days',
    booth_id: 'booth_id',
    status: 'status',
  };

  // Use mapped column name or fall back to the original
  const dbColumnName = columnMapping[orderBy] || orderBy;

  // Construct the where clause based on status
  let whereClause = `
    active: {_eq: true},
    _or: [
      {event_date: {_gte: $startDate, _lte: $endDate}}
      ${nulls ? `, {event_date: {_is_null: true}}` : ``}
    ]
  `;

  if (status === 'Not Done') {
    whereClause += `, status: {_nin: ["Done", "Canceled"]}`;
  } else if (status === 'Pending') {
    whereClause += `, _and: [{_or: [{status: {_in: ["New", "Ready"]}}, {status: {_is_null: true}}]}]`;
  } else if (status === 'Shipping') {
    whereClause += `, status: {_in: ["Preparing", "Outbound"]}`;
  } else if (status === 'WithCustomer') {
    whereClause += `, status: {_in: ["Delivered", "Packing"]}`;
  } else if (status === 'Inbound') {
    whereClause += `, status: {_in: ["Inbound", "Returned", "Checkin"]}`;
  } else if (status !== 'All') {
    whereClause += `, status: {_eq: $status}`;
  }

  // Add filter for new orders if is_new param is true
  if (lastSeenOrderId > 0) {
    //console.log(`Adding filter for new orders: id: {_gt: ${lastSeenOrderId}}`);
    whereClause += `, id: {_gt: ${lastSeenOrderId}}`;
  }

  const queryBody = `query ordersByDate(
  $startDate: date!,
  $endDate: date!,
  $limit: Int!,
  $offset: Int!
  ${
    status !== 'All' &&
    status !== 'Not Done' &&
    status !== 'Pending' &&
    status !== 'Shipping' &&
    status !== 'WithCustomer' &&
    status !== 'Inbound'
      ? ', $status: String'
      : ''
  }
) {
  orders(
    order_by: {${dbColumnName}: ${order}},
    limit: $limit,
    offset: $offset,
    where: {
      ${whereClause}
    }
  ) {
    ...Order
  }
  orders_aggregate(
    where: {
      ${whereClause}
    }
  ) {
    aggregate {
      count
    }
  }
}`;

  //console.log(`Query Body:`, queryBody); // Intentionally commented out the console.log for fragment as it was removed in a previous step by the model

  return gql`
    ${queryBody}
    ${fragments.order}
  `;
};

export default withRouter(Orders);
